import type { CapiumTagGroup } from './capium-tags';

/** Company contact tabs on Report Settings > Company Contact. */
export const CompanyContactType = {
  Director: 1,
  Secretary: 2,
  Solicitor: 3,
  Banker: 4,
} as const;

export const companyContactTypeNames: Record<number, string> = {
  1: 'Director',
  2: 'Secretary',
  3: 'Solicitor',
  4: 'Banker',
};

/** Screens whose related notes come from the report note catalogue. */
export enum ReportNoteSection {
  DirectorsReport = 1,
  AccountantReport = 2,
  AccountingPolicy = 3,
}

export interface CompanyReportPickItem {
  id: string;
  name: string;
}

/** ap5-a5: an accountant from Settings > Accountants, with the details the accountant's report fills in. */
export interface CompanyReportAccountant {
  id: string;
  name: string;
  address: string | null;
  postCode: string | null;
  accountingBody: string | null;
  /** BUG-015: the body's name the accountant was saved with (Other/None only). */
  accountingBodyName?: string | null;
}

/**
 * BUG-025: a Registered In option (the server's app.RegisteredInOption) and the behaviour it carries. `value` is the
 * text Company Info stores; the labels and the `show*` flags are what Company Info shows for it.
 */
export interface RegisteredInProfile {
  value: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  registeredNameLabel: string;
  registrationNumberLabel: string;
  registeredOfficeLabel: string;
  showCity: boolean;
  showCounty: boolean;
  showPostCode: boolean;
  /** How the accounts print the address: 'Postal' or 'Lines' (the two address lines only). */
  addressStyle: string;
  incorporationWording: string | null;
  defaultCurrencyCode: string | null;
  requiresCompaniesHouseNumber: boolean;
}

/** BUG-025: the reporting currency a Company Info save moved the client to. */
export interface ReportingCurrencyChange {
  code: string;
  name: string;
}

export interface CompanyReportOptions {
  registeredIn: string[];
  accountingBodies: string[];
  auditors: CompanyReportPickItem[];
  /** ap5-a5: the firm's accountants; the accountant's report picks from these. */
  accountants?: CompanyReportAccountant[] | null;
  /** BUG-025: every Registered In option with its behaviour, active or not. */
  registeredInProfiles?: RegisteredInProfile[] | null;
  /** BUG-025: the behaviour of a blank or unknown Registered In value. */
  defaultRegisteredInProfile?: RegisteredInProfile | null;
}

export interface CompanyInfoSettings {
  clientId: string;
  isSaved?: boolean;
  registeredName: string | null;
  registrationNumber: string | null;
  registeredOffice: string | null;
  /** BUG-027: the second line of the registered office; null when there is none. */
  registeredOffice2?: string | null;
  city: string | null;
  county: string | null;
  postCode: string | null;
  registeredIn: string | null;
  authCode: string | null;
  /** Read only: the client's type. */
  clientType?: number;
  /** Read only: whether this client's type can be a Community Interest Company (the server's rule). */
  canBeCommunityInterest?: boolean;
  /** BUG-027: the client record's Community Interest Company flag; omitted or null on save leaves it unchanged. */
  isCommunityInterest?: boolean | null;
  hasTradingAddress: boolean;
  tradingName: string | null;
  tradingRegistrationNumber: string | null;
  tradingOffice: string | null;
  tradingCity: string | null;
  tradingCounty: string | null;
  tradingPostCode: string | null;
  tradingRegisteredIn: string | null;
  tradingAuthCode: string | null;
  /** 3.0 parity: the trading address's second line (3.0 TradeAddress2); null when there is none. */
  tradingOffice2?: string | null;
  /**
   * 3.0 parity: "Show trading address on Company Information page?" (IsShowTradingAddress) - with a different trading
   * address, the accounts' Company Information page prints it under the registered office. Omitted or null on save
   * keeps the saved tick.
   */
  showTradingAddress?: boolean | null;
  /** BUG-025, response only: the reporting currency this save moved the client to, if it did. */
  currencyChanged?: ReportingCurrencyChange | null;
}

export interface CompanyContactOptions {
  clientId: string;
  directorsOnCompanyInfo: boolean;
  directorsShowPeriod: boolean;
  directorsShareInPercentage: boolean;
  directorsShowShares: boolean;
  directorsCurrentOnly: boolean;
  secretariesOnCompanyInfo: boolean;
  solicitorsOnCompanyInfo: boolean;
  bankersOnCompanyInfo: boolean;
}

export interface CompanyContactRow {
  id: string;
  clientId: string;
  contactType: number;
  /** Always set; for a person contact saved with name parts it is "firstName lastName". */
  fullName: string;
  /** BUG-029: null on contacts saved before name parts existed (and on Companies House imports). Never split from fullName. */
  firstName?: string | null;
  lastName?: string | null;
  addressLine: string | null;
  /** BUG-029: the second address line. */
  addressLine2?: string | null;
  city: string | null;
  /** BUG-029: the county (or region). Not the country. */
  county?: string | null;
  postCode: string | null;
  country: string | null;
  dateAppointed: string | null;
  dateCeased: string | null;
  ordinaryShares: number | null;
  utr: string | null;
  createAccount: boolean;
  createIndividualClient: boolean;
  isSelected: boolean;
  /** A partner's terms (BUG-078); null for other contacts. */
  salary?: number | null;
  interestOnCapital?: number | null;
  interestOnDrawings?: number | null;
}

export interface CompanyContactsView {
  options: CompanyContactOptions;
  contacts: CompanyContactRow[];
  /** BUG-029: the contact types entered as a first and a last name (the server's rule); a type not listed has one name. */
  personNameContactTypes?: number[];
}

export interface SaveCompanyContactPayload {
  id: string | null;
  clientId: string;
  contactType: number;
  /** For a person contact the API writes it from firstName and lastName. */
  fullName: string;
  firstName: string | null;
  lastName: string | null;
  addressLine: string | null;
  addressLine2: string | null;
  city: string | null;
  county: string | null;
  postCode: string | null;
  country: string | null;
  dateAppointed: string | null;
  dateCeased: string | null;
  ordinaryShares: number | null;
  utr: string | null;
  createAccount: boolean;
  createIndividualClient: boolean;
  salary?: number | null;
  interestOnCapital?: number | null;
  interestOnDrawings?: number | null;
}

export interface SaveCompanyContactResponse {
  contact: CompanyContactRow;
  createdAccount: boolean;
  createdClient: boolean;
}

export interface DirectorsReportSignatories {
  clientId: string;
  reportSignatoryId: string | null;
  reportSecondSignatoryId: string | null;
  balanceSheetSignatoryId: string | null;
  balanceSheetSecondSignatoryId: string | null;
}

export interface ReportNoteTextRow {
  noteKey: string;
  parentKey: string | null;
  groupNo: number;
  name: string;
  isEnabled: boolean;
  title: string;
  content: string;
  isCustomised: boolean;
  /** AP parity: the catalogue title and wording the note starts with, which "Reset Default" puts back. */
  defaultTitle?: string | null;
  defaultContent?: string | null;
}

export interface DirectorsReportView {
  signatories: DirectorsReportSignatories;
  directors: CompanyReportPickItem[];
  notes: ReportNoteTextRow[];
}

export interface ReportNoteTextInput {
  noteKey: string;
  isEnabled: boolean;
  /** False changes only the tick and keeps the saved wording. */
  setText: boolean;
  title?: string | null;
  content?: string | null;
}

export interface AccountantReportSettings {
  clientId: string;
  auditorUserId: string | null;
  firstName: string | null;
  address: string | null;
  postCode: string | null;
  accountingBody: string | null;
  /** BUG-031: the accounting body's name, entered when the body is Other/None. */
  accountingBodyName?: string | null;
  showOnCompanyInfo: boolean;
  removeDottedSignatureLine: boolean;
  firmName: string | null;
  officeLocation: string | null;
  replaceInCompanyInfo: boolean;
  replaceInReportPage: boolean;
  /** BUG-075: the auditor of audited accounts is a Registered Auditor. */
  isRegisteredAuditor?: boolean;
}

export interface AccountantReportView {
  details: AccountantReportSettings;
  notes: ReportNoteTextRow[];
}

/** Data for the note editor dialog (title + rich text content). */
export interface NoteEditorDialogData {
  heading: string;
  title: string;
  content: string;
  /** AP parity: when given, a "Reset Default" link puts this title and wording back into the editor. */
  defaultTitle?: string | null;
  defaultContent?: string | null;
  maxTitleLength?: number;
  tagGroups?: CapiumTagGroup[];
}

export interface NoteEditorDialogResult {
  title: string;
  content: string;
}

/** Data for the fixed-wording policy dialogs (financial instruments, capitalisation of borrowing costs). */
export interface PolicyTextDialogData {
  content: string;
}

export interface CompanyContactDialogData {
  clientId: string;
  contactType: number;
  contact?: CompanyContactRow | null;
  /** BUG-029: this contact type takes a first and a last name (from `CompanyContactsView.personNameContactTypes`). */
  usesPersonName?: boolean;
  /** Names a director-type contact as a partner (partnership) or member (LLP) instead; BUG-078. */
  principalLabel?: string;
  /** Show the partner's share / salary / interest fields. */
  partnerTerms?: boolean;
  /** An LLP member's share is always a percentage of the LLP, not a count of shares. */
  shareAsPercentage?: boolean;
}
